// Iter 82.7 / Phase E2.3 — Anomaly Report.
// Aggregations (by_type / by_severity / by_status / top employees / top departments)
// + filterable list + CSV + XLSX exports + drill-down to Anomalies Inbox.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, FileXls, FunnelSimple, ArrowSquareOut, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const SEV_TONE = {
    Critical: "bg-rose-100 text-rose-800",
    High: "bg-orange-100 text-orange-800",
    Med: "bg-amber-100 text-amber-800",
    Low: "bg-slate-100 text-slate-700",
};

const STATUS_TONE = {
    open: "bg-rose-100 text-rose-800",
    regularization_pending: "bg-amber-100 text-amber-800",
    regularized: "bg-violet-100 text-violet-800",
    acknowledged: "bg-sky-100 text-sky-800",
    closed: "bg-slate-100 text-slate-700",
};

function thirtyDaysAgo() {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
}
function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function AttendanceAnomalyReport() {
    const [filters, setFilters] = useState({
        from_date: thirtyDaysAgo(),
        to_date: todayIso(),
        anomaly_type: "",
        severity: "",
        status_filter: "",
        department: "",
    });
    const [data, setData] = useState({ items: [], summary: {} });
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: payload } = await api.get("/attendance/report/anomalies", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load anomaly report"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { reload(); }, [reload]);

    const download = async (ext) => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: blob } = await api.get(`/attendance/report/anomalies/export.${ext}`, { params, responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `attendance-anomalies-${todayIso()}.${ext}`; a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`)); }
    };

    const s = data.summary || {};

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="att-anom-page">
            <PageHeader
                eyebrow="Attendance Reports"
                title="Anomaly Report"
                description={`${data.total || 0} anomal${data.total === 1 ? "y" : "ies"} matching filters`}
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="att-anom-csv-btn">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button size="sm" onClick={() => download("xlsx")} data-testid="att-anom-xlsx-btn">
                            <FileXls size={14} className="mr-1" /> XLSX
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="att-anom-filters">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">From Date</Label>
                        <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="anom-from" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">To Date</Label>
                        <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="anom-to" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Type</Label>
                        <Input value={filters.anomaly_type} onChange={(e) => setFilters({ ...filters, anomaly_type: e.target.value })} placeholder="e.g. late_arrival" data-testid="anom-type" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Severity</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={filters.severity}
                                onChange={(e) => setFilters({ ...filters, severity: e.target.value })} data-testid="anom-sev">
                            <option value="">All</option>
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Med">Medium</option>
                            <option value="Low">Low</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Status</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2" value={filters.status_filter}
                                onChange={(e) => setFilters({ ...filters, status_filter: e.target.value })} data-testid="anom-status">
                            <option value="">All</option>
                            <option value="open">Open</option>
                            <option value="regularization_pending">Regularization Pending</option>
                            <option value="regularized">Regularized</option>
                            <option value="acknowledged">Acknowledged</option>
                            <option value="closed">Closed</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <Button variant="outline" size="sm" className="w-full"
                                onClick={() => setFilters({ from_date: thirtyDaysAgo(), to_date: todayIso(), anomaly_type: "", severity: "", status_filter: "", department: "" })}
                                data-testid="anom-clear">
                            <FunnelSimple size={14} className="mr-1" /> Clear
                        </Button>
                    </div>
                </div>
            </section>

            <section className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="anom-by-type">
                    <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">By Anomaly Type</h4>
                    {Object.keys(s.by_type || {}).length === 0 ? (
                        <div className="text-xs text-slate-400 py-3">No data</div>
                    ) : (
                        <ul className="space-y-1">
                            {Object.entries(s.by_type || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                                <li key={k} className="flex justify-between text-xs"><span className="text-slate-700">{k.replaceAll("_", " ")}</span><span className="font-mono font-semibold">{v}</span></li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="anom-by-severity">
                    <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">By Severity</h4>
                    {Object.keys(s.by_severity || {}).length === 0 ? (
                        <div className="text-xs text-slate-400 py-3">No data</div>
                    ) : (
                        <ul className="space-y-1">
                            {Object.entries(s.by_severity || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                                <li key={k} className="flex justify-between text-xs items-center">
                                    <Badge className={`text-[10px] ${SEV_TONE[k] || "bg-slate-100"}`}>{k}</Badge>
                                    <span className="font-mono font-semibold">{v}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="anom-top-employees">
                    <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Top 5 Employees</h4>
                    {(s.top_employees || []).length === 0 ? (
                        <div className="text-xs text-slate-400 py-3">No data</div>
                    ) : (
                        <ul className="space-y-1">
                            {(s.top_employees || []).map((e) => (
                                <li key={e.key} className="flex justify-between text-xs"><span className="text-slate-700 font-mono">{e.key}</span><span className="font-mono font-semibold">{e.count}</span></li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="att-anom-table">
                {loading ? (
                    <div className="text-center py-8 text-sm text-slate-400">Loading…</div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-12 text-sm text-slate-400">No anomalies match the filters.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Date</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-left px-3 py-2 font-medium">Severity</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Department</th>
                                <th className="text-left px-3 py-2 font-medium">Drill</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.slice(0, 200).map((a) => (
                                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`anom-row-${a.id}`}>
                                    <td className="px-3 py-2 text-xs font-mono">{a.attendance_date}</td>
                                    <td className="px-3 py-2 text-xs">{a.anomaly_type.replaceAll("_", " ")}</td>
                                    <td className="px-3 py-2"><Badge className={`text-xs ${SEV_TONE[a.severity] || "bg-slate-100"}`}>{a.severity}</Badge></td>
                                    <td className="px-3 py-2"><Badge className={`text-xs ${STATUS_TONE[a.status] || "bg-slate-100"}`}>{a.status}</Badge></td>
                                    <td className="px-3 py-2">
                                        <div className="text-sm font-semibold">{a.employee_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{a.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{a.department || "—"}</td>
                                    <td className="px-3 py-2">
                                        <Link to="/attendance/anomalies?scope=all" className="text-xs text-sky-700 hover:underline flex items-center gap-1" data-testid={`anom-drill-${a.id}`}>
                                            <ArrowSquareOut size={12} /> Inbox
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
